<template>
	<div
		class="inline-flex flex-col p-4 border rounded-xl bg-theme-paper border-divider-theme-12 shadow-sm"
		:class="job.priority <= 25 || hasApplied ? 'gap-1' : 'gap-3'"
		v-bind="$attrs" itemprop="item" itemscope itemtype="https://schema.org/JobPosting">
		<div class="flex justify-between w-full">
			<div class="flex items-center gap-2">
				<div class="flex items-center justify-center w-8 h-8 overflow-hidden shadow rounded-xl shrink-0">
					<img v-if="job?.organization?.avatar_image_url?.url"
						:src="path + '' + getMediaUrl(job.organization.avatar_image_url, true)" :alt="job?.organization?.name"
						loading="lazy" class="w-full h-full" @error="$event.target.src = '/images/ApplicationLogo.svg'">
					<img v-else src="/images/ApplicationLogo.svg" alt="Default logo" class="w-full h-full">
				</div>
				<div class="grid">
					<div class='flex items-center justify-between w-full overflow-hidden'>
						<Link v-if="clickRegistration"
							@click.prevent="$emit('link-clicked', route('job.view', { job: job?.slug || job?.id }), job?.id)"
							href="#" class="grid w-full">
						<h6 class="self-stretch w-full text-sm font-semibold leading-normal text-theme-primary truncate"
							itemprop="title">
							{{ job.title }}
						</h6>
						</Link>
						<Link v-else :href="route('job.view', { job: job?.slug || job?.id })" class="grid w-full">
						<h6 class="self-stretch w-full text-sm font-semibold leading-normal text-theme-primary truncate"
							itemprop="title">
							{{ job.title }}
						</h6>
						</Link>
					</div>
					<h5 class="text-xs text-theme-disabled" itemprop="hiringOrganization" itemscope
						itemtype="https://schema.org/Organization">
						<span itemprop="name">{{ job?.organization?.name }}</span>
					</h5>
				</div>
			</div>
			<div v-if="!fromWidget" class="flex">
				<button class="flex items-center justify-center w-6 h-6 cursor-pointer"
					:class="['group/bookmark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-main-lighter', { active: isBookmarked }]"
					@click="saveThisJob(job.id)">
					<Icon type="bookmark"
						class="group-[&.active]/bookmark:fill-main group-hover/bookmark:fill-main group-[&:hover.active]/bookmark:fill-transparent" />
				</button>
				<tippy v-if="!compact && canManageJob(job)">
					<Dropdown align="right" width="48" contentClasses="gap-px grid">
						<template #trigger>
							<button type="button"
								outerClass="w-6 h-6">
								<div class="cursor-pointer">
									<Icon class="h-3" type="3dots" />
								</div>
							</button>
						</template>
						<template #content>
							<GenericButton v-if="$page?.props?.isSuperUser" type="button" @click="dialogOpen(job.slug)"
								theme="warning"

								:text="$t('Remove job')"
              outerClass="block w-full px-4 py-2 text-sm leading-5 transition ease-in-out duration-fast focus-visible:outline-none focus-visible:bg-system focus-visible:text-theme-paper"/>
							<GenericButton v-if="canShowArchive(job)" @click="archiveModalOpen(job.slug)"

								:text="$t('Archive job')"
              outerClass="block w-full px-4 py-2 text-sm leading-5 transition ease-in-out duration-fast focus-visible:outline-none focus-visible:bg-theme-neutral"/>
							<Link :href="route('job.edit', { job: job.slug })"
								class="block w-full px-4 py-2 text-sm leading-5 text-center transition ease-in-out rounded-lg cursor-pointer duration-fast text-theme-primary hover:bg-theme-neutral focus-visible:outline-none focus-visible:bg-theme-neutral">
							{{
								$t("Edit")
							}}
							</Link>

						</template>
					</Dropdown>
					<template #content>
						{{ $t('More') }}
					</template>
				</tippy>
			</div>
		</div>
		<div v-if="job.priority <= 25 || hasApplied" class="flex items-center justify-end w-full h-5 gap-1 font-bold text-micro">
			<div v-if="job.priority <= 25" class="py-0.5 px-1.5 rounded-md" style="background-color: var(--color-warning-light); color: var(--color-amber-700) ;">
				{{ $t('Promoted') }}
			</div>
			<div v-if="hasApplied" class="py-0.5 px-1.5 rounded-md" style="background-color: color-mix(in srgb, var(--color-success) 25%, transparent); color: var(--color-success-dark) ;">
				{{ $t('Applied') }}
			</div>
		</div>
		<div class="flex items-center justify-between gap-2">
			<div v-if="canSeeApplicantCount || job.unread_messages_count" class="flex items-center gap-x-1">
				<div v-if="canSeeApplicantCount" class="flex items-center gap-0.5">
					<div class="text-xs text-theme-disabled">{{ countAppliedCandidates }}</div>
					<span class="text-xs text-theme-disabled"> {{ $t('Candidates') }}</span>
				</div>
				<div v-if="job.unread_messages_count"
					class="w-fit px-1.5 py-1 bg-system-dark bg-opacity-20 rounded-md justify-start items-center gap-1.5 flex">
					<div class="text-xs font-bold leading-tight text-center text-system-dark">
						{{
							job.unread_messages_count
						}}
					</div>
				</div>
			</div>
			<div class="flex items-center gap-1"
				:class="canSeeApplicantCount || job.unread_messages_count ? 'justify-end' : 'flex-1 justify-between'">
				<div v-if="!compact" class="text-xs font-normal leading-normal text-center text-main">{{
					$t(employmentTypeLabelKey(job.employment_type))
				}}
				</div>
				<div class="ml-auto text-xs text-theme-disabled">
					{{ formatTimeAgo(job?.post_date) }}
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import Dropdown from '@/Components/Dropdown.vue';
import { EXPERIENCE_LABEL_KEYS, employmentTypeLabelKey } from '@/Constants/jobEnums.js';
	import GenericButton from '@/Components/GenericButton.vue';
	import Icon from '@/Icons/OptimizedIcons.vue';
	import { Link, router } from "@inertiajs/vue3";
	import axios from 'axios';
	import moment from 'moment';
	import Swal from 'sweetalert2';
	import { Tippy } from 'vue-tippy';
	import { hasPermissions } from "@/Utils/permissions.js";
	import { jobDeleteConfirmationMessage } from '@/Composables/useJobDeleteConfirmation.js';
	import { getMediaUrl } from "@/Utils/imageUtils.js";

	export default {
		props: {
			job: Object,
			userOrganizationId: Number,
			fromWidget: {
				type: Boolean,
				default: false
			},
			compact: {
				type: Boolean,
				default: false
			},
			clickRegistration: {
				type: Boolean,
				default: false
			}
		},
		setup() {
			return {
				getMediaUrl,
				employmentTypeLabelKey
			}
		},
		components: {
			Link,
			Icon,
			GenericButton,
			Dropdown,
			Tippy
		},
		data() {
			return {
				exp: EXPERIENCE_LABEL_KEYS,
				path: import.meta.env.VITE_APP_URL,
				dialog: false,
				archiveDialog: false,
				slug: '',
				isBookmarked: false
			}
		},
		created() {
			if (this.$page.props.auth?.user?.id && this.job?.saved_by_users) {
				this.isBookmarked = this.job.saved_by_users.some(user => user.id === this.$page.props.auth.user.id);
			}
		},
		inject: ['showToastGlobal'],
		computed: {
			formattedDate() {
				return function (date) {
					if (!date) return '-';
					return moment(new Date(date)).format('DD MMM YYYY');
				};
			},
			canSeeApplicantCount() {
				return Boolean(this.$page?.props?.isSuperUser)
					|| Boolean(this.$page?.props?.auth?.organization?.id && hasPermissions(this.USER_PERMISSIONS.HR_PORTAL));
			},
			countAppliedCandidates() {
				return this.job.applications_count || (this.job?.applied_candidates?.length ?? 0);
			},
			hasApplied() {
				return this.job?.applied_candidates?.some(candidate => candidate.user_id === this.$page?.props?.auth?.user?.id);
			}

		},
		methods: {
			hasPermissions,
			formatTimeAgo(date) {
				if (!date) return '';
				return moment(date).fromNow();
			},
			canManageJob(job) {
				return this.$page?.props?.isSuperUser
					|| (this.$page?.props?.auth?.organization?.id === job?.organization_id && hasPermissions(this.USER_PERMISSIONS.HR));
			},
			canShowArchive(job) {
				return (this.$page?.props?.isSuperUser || this.$page?.props?.auth?.permissions?.includes('hr')) && job.status != 'archived';
			},
			truncate(text) {
				return text.length > 32 ? text.substring(0, 29) + '...' : text;
			},
			saveThisJob(jobId) {
				if (!this.$page.props.auth.user) {
					Swal.fire({
						title: "Error",
						toast: true,
						text: this.$t("Please login to save this job"),
						icon: "error",
                        position: 'top-end',
						showConfirmButton: false,
						timer: 2000
					})
				}
				const data = { jobId: jobId }
				const currentUrl = window.location.href;
				axios.post(route('jobs.save'), data).then((response) => {
					if (response.data.unSaved == true) {
						this.isBookmarked = false;
						this.job.saved_by_users = [];
						if (currentUrl.includes('/saved')) {
							this.$emit('job-unsaved', jobId);
						}
					} else {
						this.isBookmarked = true;
						this.job.saved_by_users.push(this.$page.props.auth.user)
					}
					Swal.fire({
						title: "Success",
						toast: true,
						text: response.data.unSaved == true ? this.$t("Job unsaved successfully") : this.$t("Job saved successfully"),
						icon: "success",
                        position: 'top-end',
						showConfirmButton: false,
						timer: 2000
					});

				}).catch((error) => {
					console.log(error);
				})
			},
			dialogOpen(slug) {
				this.$emit('open-dialog', {
					title: 'Confirmation',
					message: jobDeleteConfirmationMessage(this.job, this.$page?.props?.vonqAutoRemoveEnabled),
					onConfirm: () => this.removeJob(slug)
				});
			},
			archiveModalOpen(slug) {
				this.$emit('open-dialog', {
					title: 'Confirmation',
					message: 'Are you sure you want to archive this job?',
					onConfirm: () => this.archiveJob(slug)
				});
			},
			removeJob(slug) {
				router.delete(route('job.delete', slug || this.slug));
			},
			archiveJob(slug) {
				router.put(route('job.archive', slug || this.slug), {}, {
					onSuccess: (response) => {
						this.showToastGlobal('Job archived successfully');
						// Emit event to parent to remove the job from the list
						this.$emit('job-archived', this.job.id);
						// If we're on the active jobs page, stay there, otherwise redirect
						const currentUrl = window.location.href;
						if (!currentUrl.includes('/active')) {
							router.get(route('job-list', { type: 'active' }));
						}
					},
					onError: (err) => {
						this.showToastGlobal('An error occurred while archiving the job.', 'error');
						console.error('Error details:', err);
					}
				});
			}
		}
	}

</script>
